import { useEffect, useRef, useState } from "react";
import { Download, X, Share, Plus, MonitorDown } from "lucide-react";

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isMobile() {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

export function InstallAppButton({ className = "", label = "Install App" }) {
  const promptRef = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); promptRef.current = e; };
    const onInstalled = () => { promptRef.current = null; };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const click = async () => {
    if (promptRef.current) {
      promptRef.current.prompt();
      await promptRef.current.userChoice;
      promptRef.current = null;
    } else {
      setShow(true);
    }
  };

  const ios = isIOS();
  const mobile = isMobile();

  return (
    <>
      <button onClick={click} data-testid="install-app-btn"
        className={className || "flex items-center gap-3 px-6 py-3 w-full text-black hover:bg-sand transition-colors"}>
        <Download size={17} strokeWidth={1.6} />
        <span className="font-sans text-sm">{label}</span>
      </button>

      {show && (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4" data-testid="install-app-sheet">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => setShow(false)} />
          <div className="relative bg-bone w-full max-w-sm p-8 border border-gold/40">
            <button onClick={() => setShow(false)} data-testid="install-app-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            {mobile ? <Download size={30} className="text-gold" strokeWidth={1.5} /> : <MonitorDown size={30} className="text-gold" strokeWidth={1.5} />}
            <h3 className="font-serif text-2xl tracking-tight mt-4">Save this app to your device</h3>
            <p className="font-sans text-sm text-charcoal/60 mt-1">Add the ministry app icon to your {mobile ? "home screen" : "desktop"} for one-tap access.</p>
            <ol className="mt-5 space-y-4 font-sans text-sm text-charcoal/75">
              {ios ? (
                <>
                  <li className="flex items-center gap-3"><Step n={1} /> Tap the <Share size={15} className="inline text-navy" /> Share button in Safari.</li>
                  <li className="flex items-center gap-3"><Step n={2} /> Choose <Plus size={15} className="inline text-navy" /> "Add to Home Screen".</li>
                  <li className="flex items-center gap-3"><Step n={3} /> Confirm — the icon appears on your home screen.</li>
                </>
              ) : mobile ? (
                <>
                  <li className="flex items-center gap-3"><Step n={1} /> Open your browser menu (⋮).</li>
                  <li className="flex items-center gap-3"><Step n={2} /> Tap "Add to Home screen" / "Install app".</li>
                  <li className="flex items-center gap-3"><Step n={3} /> Confirm — the icon appears on your home screen.</li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-3"><Step n={1} /> Look for the install icon in your browser's address bar (Chrome / Edge).</li>
                  <li className="flex items-center gap-3"><Step n={2} /> Or open the browser menu and choose "Install In His Image…".</li>
                  <li className="flex items-center gap-3"><Step n={3} /> The app opens in its own window with our icon.</li>
                </>
              )}
            </ol>
          </div>
        </div>
      )}
    </>
  );
}

function Step({ n }) {
  return <span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">{n}</span>;
}
